import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase.js";
import { useAuth, useReadOnly } from "../lib/auth.js";
import { BookingDetailsModal } from "./BookingDetails.js";
import { BookingActionHost, bookingQuickActions, hasCombinableOnSameDay, type BookingAction } from "./Bookings.js";
import { QuickActions } from "../lib/QuickActions.js";
import { formatTime as formatClockTime, formatWeekdayDayMonth } from "../lib/datetime.js";
import { addDaysYmd, endOfDayExclusive, startOfDayInZone, todayInZone, weekdayOfYmd, ymdOfInstant } from "../lib/zone.js";
import { Badge } from "../lib/ui.js";
import { useLiveRefresh } from "../lib/useLiveRefresh.js";
import { CompleteBookingModal, bookingTotal, canMarkCompleted } from "../lib/CompleteBookingModal.js";
import { CallsToReviewTile } from "./CallsToReview.js";
import { StatTile } from "../lib/StatTile.js";

const STATUS_TONE: Record<string, "success" | "danger" | "neutral" | "warning"> = {
  confirmed: "success",
  cancelled: "danger",
  completed: "neutral",
  no_show: "warning",
};

interface TodayBookingRow {
  id: string;
  starts_at: string;
  status: string;
  customer_name: string | null;
  /** Who the booking belongs to and which staff member it is with: what decides if Combine is offered. */
  customer_id: string | null;
  guest_name: string | null;
  resource_id: string | null;
  ends_at: string;
  service_name: string | null;
  resource_name: string | null;
  /** Prices of the visit's services, in order (null = that service has no price). */
  prices: Array<number | null>;
}

interface TodayCallRow {
  id: string;
  caller_number: string | null;
  started_at: string | null;
  duration_seconds: number | null;
  outcome: string | null;
  transfer_state: string | null;
}


/** Weekdays (0 = Sunday) the business works: its own opening hours if set, otherwise the days any
 *  staff member has availability (what the booking engine actually uses). Empty = nothing set up. */
export function workingWeekdays(businessHours: Array<{ weekday: number }>, staffAvailability: Array<{ weekday: number }>): Set<number> {
  const source = businessHours.length > 0 ? businessHours : staffAvailability;
  return new Set(source.map((r) => r.weekday));
}

/** The next day after today the business is open: a working weekday not wholly covered by a
 *  business-wide holiday. No working days set up anywhere = every day counts as open. Falls back
 *  to tomorrow if nothing qualifies in the next two weeks. "Today" and every day are the business's
 *  own (lib/zone.ts); the result is that day's midnight at the business, as an instant. */
export function nextWorkingDay(openWeekdays: Set<number>, holidays: Array<{ starts_at: string; ends_at: string }>, today: string = todayInZone()): Date {
  for (let n = 1; n <= 14; n++) {
    const ymd = addDaysYmd(today, n);
    const day = startOfDayInZone(ymd);
    const end = endOfDayExclusive(ymd);
    if (openWeekdays.size > 0 && !openWeekdays.has(weekdayOfYmd(ymd))) continue;
    if (holidays.some((h) => new Date(h.starts_at) <= day && new Date(h.ends_at) >= end)) continue;
    return day;
  }
  return startOfDayInZone(addDaysYmd(today, 1));
}

function formatTime(iso: string | null): string {
  return iso ? formatClockTime(iso) : "-";
}

/**
 * "Today" is the browser's local day, same simplification the rest of the client portal
 * uses (nothing here reads businesses.timezone) -- fine for a single-timezone Australian
 * SMB, revisit if/when multi-region businesses show up.
 */
/** The tables behind everything on the Dashboard; a change to any of them for this business refreshes it. */
const LIVE_TABLES = ["booking_items", "bookings", "calls", "callback_requests", "review_flags"] as const;

export function Dashboard() {
  const { activeBusinessId } = useAuth();
  const [bookings, setBookings] = useState<TodayBookingRow[]>([]);
  const [nextDayBookings, setNextDayBookings] = useState<TodayBookingRow[]>([]);
  const [nextDay, setNextDay] = useState<Date>(() => new Date());
  const [hideCancelled, setHideCancelled] = useState(true);
  const [calls, setCalls] = useState<TodayCallRow[]>([]);
  const [openCallbacks, setOpenCallbacks] = useState(0);
  const [loading, setLoading] = useState(true);
  // Bumped whenever something changes (a booking made on a call, a callback, coming back to the app...): re-read quietly.
  const [refreshTick, setRefreshTick] = useState(0);
  const [completeTarget, setCompleteTarget] = useState<TodayBookingRow | null>(null);
  const [detailsBookingId, setDetailsBookingId] = useState<string | null>(null);
  const [bookingAction, setBookingAction] = useState<{ bookingId: string; action: BookingAction } | null>(null);
  const loadedFor = useRef<string | null>(null);
  useLiveRefresh(activeBusinessId, LIVE_TABLES, () => setRefreshTick((t) => t + 1));

  useEffect(() => {
    if (!activeBusinessId) return;
    let cancelled = false;
    // Only the first load of a business shows "Loading..."; later refreshes swap the numbers in place.
    if (loadedFor.current !== activeBusinessId) setLoading(true);

    // Days are the business's own: today starts at the business's midnight, whatever zone this browser is in.
    const today = todayInZone();
    const todayStart = startOfDayInZone(today).toISOString();
    const tomorrowDate = startOfDayInZone(addDaysYmd(today, 1));
    const tomorrowStart = tomorrowDate.toISOString();
    const horizon = startOfDayInZone(addDaysYmd(today, 15));

    let nextDayDate = tomorrowDate;
    let nextStart = tomorrowStart;

    Promise.all([
      supabase.from("business_hours").select("weekday").eq("business_id", activeBusinessId),
      supabase.from("availability").select("weekday").eq("business_id", activeBusinessId),
      supabase
        .from("availability_exceptions")
        .select("starts_at, ends_at")
        .eq("business_id", activeBusinessId)
        .eq("kind", "holiday")
        .is("resource_id", null)
        .gte("ends_at", todayStart)
        .lt("starts_at", horizon.toISOString()),
    ])
      .then(([hoursRes, availabilityRes, holidayRes]) => {
        const openWeekdays = workingWeekdays((hoursRes.data ?? []) as Array<{ weekday: number }>, (availabilityRes.data ?? []) as Array<{ weekday: number }>);
        nextDayDate = nextWorkingDay(openWeekdays, (holidayRes.data ?? []) as Array<{ starts_at: string; ends_at: string }>);
        nextStart = nextDayDate.toISOString();
        const nextEndDate = endOfDayExclusive(ymdOfInstant(nextDayDate));
        return Promise.all([
          supabase
            .from("booking_items")
            .select(
              "id, booking_id, starts_at, ends_at, resource_id, cancelled_at, bookings(status, customer_id, guest_name, customers(name)), services(name, price_cents), resources(name)"
            )
            .eq("business_id", activeBusinessId)
            .gte("starts_at", todayStart)
            .lt("starts_at", nextEndDate.toISOString())
            .order("starts_at"),
          supabase
            .from("v_call_list")
            .select("id, caller_number, started_at, duration_seconds, outcome, transfer_state")
            .eq("business_id", activeBusinessId)
            .gte("started_at", todayStart)
            .lt("started_at", tomorrowStart)
            .order("started_at", { ascending: false }),
          supabase
            .from("callback_requests")
            .select("id", { count: "exact", head: true })
            .eq("business_id", activeBusinessId)
            .eq("status", "open"),
        ]);
      })
      .then(([bookingRes, callRes, callbackRes]) => {
      if (cancelled) return;
      // A booking with several services is one visit: one row, services listed in order, starting at its first service.
      const byBooking = new Map<string, TodayBookingRow & { services: string[]; staff: string[] }>();
      for (const i of (bookingRes.data ?? []) as any[]) {
        // One service of a still-confirmed visit can be cancelled on its own; it is no longer part of the visit
        // (the Bookings list leaves it out the same way). A cancelled booking keeps showing what it had.
        if (i.cancelled_at && i.bookings?.status === "confirmed") continue;
        const row =
          byBooking.get(i.booking_id) ??
          {
            id: i.booking_id,
            starts_at: i.starts_at,
            status: i.bookings?.status ?? "-",
            customer_name: i.bookings?.guest_name ?? i.bookings?.customers?.name ?? null,
            customer_id: i.bookings?.customer_id ?? null,
            guest_name: i.bookings?.guest_name ?? null,
            resource_id: i.resource_id ?? null,
            ends_at: i.ends_at,
            service_name: null,
            resource_name: null,
            prices: [] as Array<number | null>,
            services: [] as string[],
            staff: [] as string[],
          };
        if (i.ends_at > row.ends_at) row.ends_at = i.ends_at;
        if (i.services?.name) row.services.push(i.services.name);
        row.prices.push(i.services?.price_cents ?? null);
        if (i.resources?.name && !row.staff.includes(i.resources.name)) row.staff.push(i.resources.name);
        byBooking.set(i.booking_id, row);
      }
      const bookingRows: TodayBookingRow[] = [...byBooking.values()].map(({ services, staff, ...row }) => ({
        ...row,
        service_name: services.join(" + ") || null,
        resource_name: staff.join(", ") || null,
      }));
      setBookings(bookingRows.filter((b) => b.starts_at < tomorrowStart));
      setNextDayBookings(bookingRows.filter((b) => b.starts_at >= nextStart));
      setNextDay(nextDayDate);
      setCalls((callRes.data ?? []) as TodayCallRow[]);
      setOpenCallbacks(callbackRes.count ?? 0);
      loadedFor.current = activeBusinessId;
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [activeBusinessId, refreshTick]);

  if (loading) return <p className="rc-loading">Loading...</p>;
  const everyBooking = [...bookings, ...nextDayBookings];
  const tableActions = { canCombine: (b: TodayBookingRow) => hasCombinableOnSameDay(b, everyBooking), onView: (b: TodayBookingRow) => setDetailsBookingId(b.id), onAction: (b: TodayBookingRow, action: BookingAction) => setBookingAction({ bookingId: b.id, action }) };

  const activeBookings = bookings.filter((b) => b.status !== "cancelled");
  const shownBookings = hideCancelled ? activeBookings : bookings;
  const shownNextDay = hideCancelled ? nextDayBookings.filter((b) => b.status !== "cancelled") : nextDayBookings;
  const missedOrTransferred = calls.filter((c) => c.transfer_state && c.transfer_state !== "none").length;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 className="rc-h1" style={{ marginBottom: 2 }}>Dashboard</h1>
        <div style={{ color: "var(--rc-text-muted)", fontSize: 13.5 }}>{formatWeekdayDayMonth(new Date())}</div>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 32, flexWrap: "wrap" }}>
        <StatTile label="Calls today" value={calls.length} />
        <StatTile label="Bookings today" value={activeBookings.length} />
        <StatTile label="Transfers today" value={missedOrTransferred} />
        <StatTile label="Open callbacks" value={openCallbacks} to="/app/callbacks" attention={openCallbacks > 0} />
        {activeBusinessId && <CallsToReviewTile businessId={activeBusinessId} refreshTick={refreshTick} />}
      </div>

      <section style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "4px 16px" }}>
          <h2 className="rc-h2" style={{ marginBottom: 12 }}>Today's bookings</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 13, color: "var(--rc-text-muted)" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={hideCancelled} onChange={(e) => setHideCancelled(e.target.checked)} />
              Hide cancelled
            </label>
            <Link to="/app/bookings" style={{ fontWeight: 600 }}>
              View all bookings
            </Link>
          </div>
        </div>
        <BookingsTable rows={shownBookings} total={bookings.length} hideCancelled={hideCancelled} emptyLabel="No bookings today." bold onComplete={(b) => setCompleteTarget(b)} {...tableActions} />
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2 className="rc-h2" style={{ marginBottom: 12 }}>
          Next working day's bookings
          <span style={{ fontWeight: 400, fontSize: 13, color: "var(--rc-text-muted)", marginLeft: 8 }}>
            {formatWeekdayDayMonth(nextDay)}
          </span>
        </h2>
        <BookingsTable rows={shownNextDay} total={nextDayBookings.length} hideCancelled={hideCancelled} emptyLabel="No bookings on the next working day." {...tableActions} />
      </section>

      {detailsBookingId && <BookingDetailsModal bookingId={detailsBookingId} onClose={() => setDetailsBookingId(null)} />}

      {bookingAction && activeBusinessId && (
        <BookingActionHost
          businessId={activeBusinessId}
          bookingId={bookingAction.bookingId}
          action={bookingAction.action}
          onClose={() => setBookingAction(null)}
          onChanged={() => setRefreshTick((t) => t + 1)}
        />
      )}

      {completeTarget && (
        <CompleteBookingModal
          bookingId={completeTarget.id}
          customer={completeTarget.customer_name}
          service={completeTarget.service_name ?? "booking"}
          startsAt={completeTarget.starts_at}
          {...bookingTotal(completeTarget.prices)}
          onClose={() => setCompleteTarget(null)}
          onDone={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </div>
  );
}

function BookingsTable({
  rows,
  total,
  hideCancelled,
  emptyLabel,
  bold = false,
  onComplete,
  onView,
  canCombine,
  onAction,
}: {
  rows: TodayBookingRow[];
  total: number;
  hideCancelled: boolean;
  emptyLabel: string;
  bold?: boolean;
  /** When given, a confirmed booking whose visit has started gets a "Mark completed" action (today's table only). */
  onComplete?: (b: TodayBookingRow) => void;
  /** The quick-action menu on each row (the same wording as the Bookings list). */
  onView: (b: TodayBookingRow) => void;
  canCombine: (b: TodayBookingRow) => boolean;
  onAction: (b: TodayBookingRow, action: BookingAction) => void;
}) {
  const readOnly = useReadOnly();
  return (
    <div className="rc-table-scroll">
      <table className="rc-stack" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
            <th style={{ padding: "10px 12px" }}>Time</th>
            <th style={{ padding: "10px 12px" }}>With</th>
            <th style={{ padding: "10px 12px" }}>Customer</th>
            <th style={{ padding: "10px 12px" }}>Service</th>
            <th style={{ padding: "10px 12px" }}>Status</th>
            <th style={{ padding: "10px 12px" }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
              <td data-label="Time" style={{ padding: "10px 12px", fontWeight: bold ? 700 : 400 }}>{formatTime(b.starts_at)}</td>
              <td data-label="With" style={{ padding: "10px 12px", fontWeight: bold ? 700 : 400 }}>{b.resource_name ?? "-"}</td>
              <td data-label="Customer" style={{ padding: "10px 12px", fontWeight: bold ? 600 : 400 }}>{b.customer_name ?? "-"}</td>
              <td data-label="Service" style={{ padding: "10px 12px" }}>{b.service_name ?? "-"}</td>
              <td data-label="Status" style={{ padding: "10px 12px" }}>
                <Badge tone={STATUS_TONE[b.status] ?? "neutral"}>{b.status.replace("_", " ")}</Badge>
              </td>
              <td className="rc-actions" style={{ padding: "10px 12px", whiteSpace: "nowrap", textAlign: "right" }}>
                {onComplete && !readOnly && canMarkCompleted(b.status, b.starts_at) && (
                  <button onClick={() => onComplete(b)} style={{ background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, marginRight: 12, fontSize: 13 }}>
                    Mark completed
                  </button>
                )}
                <QuickActions
                  actions={bookingQuickActions(b.status, { view: () => onView(b), act: (a) => onAction(b, a), canCombine: canCombine(b), readOnly })}
                  label={`Actions for ${b.customer_name ?? "booking"}`}
                />
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} style={{ padding: 16, color: "var(--rc-text-faint)", fontWeight: 400 }}>
                {emptyLabel}
                {hideCancelled && total > 0 && ` (${total} cancelled hidden)`}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
